import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, Input, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {EventDTO, XploreItemDTO} from "../../../shared/object/xplorer-be-parsed-classes";
import { ReservationService } from "../../../services/reservation.service";
import { AuthService } from "../../../services/auth/auth.service";
import { ToastService } from "../../../services/toast/toast.service";
import {Router} from "@angular/router";
import { TranslateModule } from '@ngx-translate/core';
import { ReservationPopupComponent } from '../reservation-popup/reservation-popup.component';


@Component({
    selector: 'book-section',
    templateUrl: './book-section.component.html',
    styleUrls: ['./book-section.component.css'],
    imports: [ReservationPopupComponent, TranslateModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BookSectionComponent implements OnInit {

  @Input()
  xploreItem!: XploreItemDTO;

  hasUserReserved: boolean = false;
  availableReservations: number = 0;
  isLoading: boolean = true;
  toggleModal: boolean = false;
  isMobile: boolean = false;

  private readonly reservationService: ReservationService = inject(ReservationService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly router: Router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);


  ngOnInit(): void {
    this.isMobile = this.isBrowser ? window.innerWidth < 768 : false;
    if (this.isBookable) {
      this.initAvailableReservationsNumber();
    }
    this.initAlreadyReserved();
  }

  private initAvailableReservationsNumber() {
    this.reservationService.getAvailableReservations(this.xploreItem.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
      ).subscribe({
      next: (places) => this.availableReservations = places,
      error: (error) => {
        this.toastService.showErrorToast(`Error: ${error.error?.message ?? 'Unable to load available reservations. Please try again later.'}`);
      }
    });
  }

  private initAlreadyReserved() {
    this.authService.isUserLogged()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (isUserLogged: boolean) => {
            if (!isUserLogged) {
              this.isLoading = false;
              this.hasUserReserved = false;
            } else {
              this.retrieveUserReservation();
            }
          },
          error: () => {
            this.isLoading = false;
            this.hasUserReserved = false;
          }
        });
  }

  openModal() {
    this.authService.isUserLogged()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (isUserLogged: boolean) => {
          if (!isUserLogged) {
            const redirectUrl = this.isMobile
              ? `/explore/info/${this.xploreItem.id}`
              : `/explore/${this.xploreItem.id}`;

            this.router.navigate(['login'], {state: {source: redirectUrl}});
          } else {
            this.toggleModal = !this.toggleModal;
          }
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  onReservationEvent() {
    this.isLoading = true;
    this.retrieveUserReservation();
  }

  private retrieveUserReservation() {
    this.reservationService.getReservationByUser()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (reservations) => {
          const reservationsIds = reservations.map(reservation => reservation.eventId);
          this.hasUserReserved = reservationsIds.includes(this.xploreItem.id);
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          this.toastService.showErrorToast(`Error: ${error.error?.message ?? 'Unable to check your reservation status. Please try again later.'}`);
        }
      });
  }

  get isBookable() {
    return (this.xploreItem as EventDTO).bookable;
  }

}
